/**
 * Managing your own second factor.
 *
 * Signing *in* with one lives in `provider.tsx`, next to the rest of sign-in.
 * This is the other half: setting one up, turning it off, and the printed
 * codes that get you back in when the phone is gone.
 *
 * Every one of these acts on the signed-in account and nobody else's. There is
 * deliberately no way to enrol, disable, or read codes for another user — an
 * administrator who could turn off somebody's second factor is a way around
 * it, and an administrator who could enrol one for them is a way in.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface MfaStatus {
  /** A secret exists. Says nothing about whether it is switched on. */
  enrolled: boolean;
  /**
   * A code has proved the phone received the secret, and sign-in now asks for
   * one. Enrolment that never got this far enforces nothing, on purpose:
   * otherwise closing the tab half way through locks the account.
   */
  confirmed: boolean;
  confirmed_at: string | null;
  recovery_codes_left: number;
  /** How many were issued, so a screen can say "3 of 8" rather than "3". */
  recovery_codes_total: number;
}

export interface MfaEnrolment {
  /** Base32, for typing in when a camera will not focus on the QR code. */
  secret: string;
  /** `otpauth://totp/...` — render this as the QR code. */
  provisioning_uri: string;
}

export function useMfaStatus() {
  const api = useApi();
  return useQuery({
    queryKey: ['mfa', 'status'],
    queryFn: ({ signal }) => api.get<MfaStatus>('/v1/auth/mfa', signal),
    staleTime: 30_000,
  });
}

/**
 * Generate a secret and get back what a phone needs to read it.
 *
 * Nothing is enforced yet. Calling it again before confirming replaces the
 * secret, which is what somebody who lost the QR code needs; calling it on an
 * account that already has a confirmed app is refused (409), because swapping
 * a live second factor without proving anything would be a way around it.
 */
export function useBeginMfaEnrolment() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MfaEnrolment, ApiError, void>({
    mutationFn: () => api.post<MfaEnrolment>('/v1/auth/mfa/enrol', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });
}

/**
 * Prove the phone has the secret, switch the second factor on, and receive the
 * recovery codes.
 *
 * **The codes come back once.** They are hashed on the way in, so a screen
 * that does not show them here cannot show them later — it has to say so
 * before the user navigates away.
 *
 * Not gated on the plan, although `useBeginMfaEnrolment` is: this is the
 * second half of one act, and gating both halves strands anybody whose
 * restaurant changed plan between scanning the QR code and typing the code.
 */
export function useConfirmMfaEnrolment() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ codes: string[] }, ApiError, { code: string }>({
    mutationFn: ({ code }) => api.post<{ codes: string[] }>('/v1/auth/mfa/confirm', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });
}

/**
 * Turn the second factor off. Needs a working code, or a recovery code.
 *
 * A recovery code works here on purpose: the case this exists for is a phone
 * that is gone, so there is no app left to ask.
 *
 * Not gated on the plan — a restaurant that drops to Starter has to be able to
 * turn off something it can no longer set up.
 */
export function useDisableMfa() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { code: string }>({
    mutationFn: ({ code }) => api.post<void>('/v1/auth/mfa/disable', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });
}

/**
 * A fresh set of recovery codes. The old ones stop working immediately.
 *
 * Not gated on the plan, like turning it off: somebody on Starter who has
 * spent their last recovery code is one flat battery from losing the account,
 * and a 402 here would be a paywall in front of a lockout.
 */
export function useRegenerateRecoveryCodes() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ codes: string[] }, ApiError, { code: string }>({
    mutationFn: ({ code }) =>
      api.post<{ codes: string[] }>('/v1/auth/mfa/recovery-codes', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });
}
